import type { CSSProperties, Ref } from "react";
import objectUtils from "@/common/utils/objectUtils";
import {
  addAllArticlesToFavorites,
  addArticleToFavorites,
  decrementFavoriteImageIndex,
  incrementFavoriteImageIndex,
  removeAllArticlesFromFavorites,
  removeArticleFromFavorites,
  removeFavoriteAtIndex,
  selectFavorite,
  selectFavoriteImage,
} from "@/stores/experience/actions/favorites";
import FavoritesHeader from "@/components/favorites/FavoritesHeader";
import FavoriteImagePicker from "@/components/favorites/FavoriteImagePicker";
import FavoriteImageSwiper from "@/components/favorites/FavoriteImageSwiper";
import FavoriteDetails from "@/components/favorites/FavoriteDetails";
import FavoritesChecklist from "@/components/favorites/FavoritesChecklist";
import { experienceTypes } from "@/modules/ExperienceData";
import type { TemplateSizes } from "@/types/layout";
import type { ExperienceState } from "@/types/state";

type FavoritesProps = {
  experienceState: ExperienceState;
  style?: CSSProperties;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

export default function Favorites({
  experienceState,
  style,
  nodeRef,
  onApplicationEvent,
}: FavoritesProps) {
  const experienceData = experienceState.experienceData;
  const template = experienceState.template as {
    getSizes(): TemplateSizes;
  };
  const sizes = template.getSizes().favorites;

  const lightboxStyle = {
    width: sizes.lightbox.width,
    height: sizes.lightbox.height,
    left: sizes.lightbox.margins.left,
    top: sizes.lightbox.margins.top,
  };

  const details =
    experienceData.type === experienceTypes.CHECKLIST ? (
      <FavoritesChecklist
        experienceState={experienceState}
        onApplicationEvent={onApplicationEvent}
        onAddAllArticlesToFavorites={addAllArticlesToFavorites}
        onRemoveAllArticlesFromFavorites={removeAllArticlesFromFavorites}
        onAddArticleToFavorites={addArticleToFavorites}
        onRemoveArticleFromFavorites={removeArticleFromFavorites}
      />
    ) : (
      <FavoriteDetails
        experienceState={experienceState}
        onRemoveFavoriteAtIndex={removeFavoriteAtIndex}
        onApplicationEvent={onApplicationEvent}
      />
    );

  return (
    <div
      ref={nodeRef}
      className="favorites tw:absolute tw:z-2 tw:flex tw:justify-end tw:overflow-hidden tw:bg-[rgba(0,0,0,0.8)] tw:p-[6px] tw:[.portrait_&]:p-0"
      style={objectUtils.extend({}, lightboxStyle, style)}
    >
      <div className="favorites-panel tw:relative tw:right-0 tw:flex tw:h-full tw:w-[83%] tw:flex-col tw:items-stretch tw:overflow-hidden tw:bg-light-grey tw:p-[6px] tw:[.portrait_&]:right-0 tw:[.portrait_&]:h-full tw:[.portrait_&]:w-full tw:[.portrait_&]:p-0">
        <FavoritesHeader
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
          onFavoriteSelected={selectFavorite}
        />
        <div className="favorites-body-panel tw:mt-[2px] tw:flex tw:grow tw:flex-row tw:items-stretch tw:[.portrait_&]:flex-col">
          <FavoriteImagePicker
            experienceState={experienceState}
            onFavoriteImageSelected={selectFavoriteImage}
          />
          <FavoriteImageSwiper
            experienceState={experienceState}
            onImageSelected={selectFavoriteImage}
            onIncrementFavoriteImageIndex={incrementFavoriteImageIndex}
            onDecrementFavoriteImageIndex={decrementFavoriteImageIndex}
          />
          {details}
        </div>
      </div>
    </div>
  );
}
